import {useState} from 'react';
import {Autocomplete} from '@material-ui/lab';
import {Grid, TextField} from '@material-ui/core';
import {SelectInputElement} from '../PreQualification/Components/SelectInputElement';
import {NumberFormatCustom, SwitchCustom, TextBox} from '../../Core/UI';
import {Parameter} from '../../Core/mobx/ParameterStore/ParameterStore';
import {observer} from 'mobx-react-lite';
import {Approval} from '../../Core/mobx/ParameterStore/Approval';
import {Prequal} from '../../Core/mobx/ParameterStore/Prequal';
import {fieldOptions} from '../../Core/mobx/ParameterStore/PrequalXref';
import {AGENIUM_OCUPATION_LIST} from '../constants';

type EmploymentProps = {
    parameters: Approval | Prequal;
    valueChanged: (p?: Parameter & {name?: string}, value?: any) => void;
    preapproval?: boolean;
    prequal?: boolean;
};

const required = (value?: string) => (
    <span>
        {value && value} <span style={{color: 'red'}}>*</span>
    </span>
);

const EmploymentReflexives = observer(({parameters, valueChanged}: EmploymentProps) => {
    const [inputValue, setInputValue] = useState(parameters.occupation?.value || '');
    const showPrevSelfEmployed =
        parameters.currentlyEmployed.value === 'Employed' &&
        parameters.employmentDays.value !== '' &&
        (parseInt(parameters.employmentDays.value, 10) < 186 || parseFloat(parameters.timeEmployed.value) <= 0.5);

    return (
        <>
            {parameters.currentlyEmployed.value === 'Employed' && (
                <>
                    <Grid item xs={4}>
                        <SwitchCustom
                            param={{
                                label: 'Currently self-employed',
                                ...parameters.currentSelfEmployed,
                                name: 'currentSelfEmployed',
                            }}
                            required={required('')}
                            valueChanged={valueChanged}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextBox
                            id={'nameOfEmployer'}
                            label={required('Employer Name')}
                            value={parameters.nameOfEmployer?.value}
                            onChange={(value) =>
                                valueChanged({...parameters.nameOfEmployer, name: 'nameOfEmployer'}, value)
                            }
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextBox
                            id={'timeEmployed'}
                            label={required('Time Employed (Years)')}
                            value={parameters.timeEmployed?.value}
                            onChange={(value) =>
                                valueChanged({...parameters.timeEmployed, name: 'timeEmployed'}, value)
                            }
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextBox
                            id={'employmentDays'}
                            label={'Employment Days'}
                            value={parameters.employmentDays?.value}
                            onChange={(value) =>
                                valueChanged({...parameters.employmentDays, name: 'employmentDays'}, value)
                            }
                            type="number"
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextBox
                            id={'employerAddress'}
                            label={required('Employer’s Address')}
                            value={parameters.employerAddress?.value}
                            onChange={(value) =>
                                valueChanged({...parameters.employerAddress, name: 'employerAddress'}, value)
                            }
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <Autocomplete
                            disablePortal
                            clearOnBlur
                            disableClearable
                            options={AGENIUM_OCUPATION_LIST}
                            value={parameters.occupation?.value}
                            inputValue={inputValue}
                            noOptionsText={false}
                            onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
                            onBlur={() => {
                                const hasPartialMatch = AGENIUM_OCUPATION_LIST.some((option) =>
                                    option.toLowerCase().includes(inputValue.toLowerCase()),
                                );
                                if (!hasPartialMatch) {
                                    valueChanged({...parameters.occupation, name: 'occupation'}, inputValue);
                                }
                            }}
                            onChange={(_, newValue) =>
                                valueChanged({...parameters.occupation, name: 'occupation'}, newValue)
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    aria-label="Occupation"
                                    label={required('Occupation')}
                                    variant="outlined"
                                    style={{width: '100%', marginTop: 8}}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextBox
                            id={'industryDuties'}
                            label={required('Field & Job Tasks')}
                            value={parameters.industryDuties?.value}
                            onChange={(value) =>
                                valueChanged({...parameters.industryDuties, name: 'industryDuties'}, value)
                            }
                        />
                    </Grid>
                </>
            )}
            {showPrevSelfEmployed && (
                <>
                    <Grid item xs={4}>
                        <SwitchCustom
                            param={{
                                label: 'Previously Self Employed',
                                ...parameters.prevSelfEmployed,
                                name: 'prevSelfEmployed',
                            }}
                            required={required()}
                            valueChanged={valueChanged}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextBox
                            id={'prevEmployName'}
                            label={required('Previous Employer Name')}
                            value={parameters.prevEmployName?.value}
                            onChange={(value) =>
                                valueChanged({...parameters.prevEmployName, name: 'prevEmployName'}, value)
                            }
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextBox
                            id={'prevEmployStart'}
                            label={required('Previous Employer Start Date')}
                            value={parameters.prevEmployStart?.value}
                            onChange={(value) =>
                                valueChanged({...parameters.prevEmployStart, name: 'prevEmployStart'}, value)
                            }
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextBox
                            id={'prevEmployOccup'}
                            label={required('Previous Occupation')}
                            value={parameters.prevEmployOccup?.value}
                            onChange={(value) =>
                                valueChanged({...parameters.prevEmployOccup, name: 'prevEmployOccup'}, value)
                            }
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextBox
                            id={'prevEmployIndustry'}
                            label={required('Previous Employer Work industry and Job Duties/Description')}
                            value={parameters.prevEmployIndustry?.value}
                            onChange={(value) =>
                                valueChanged({...parameters.prevEmployIndustry, name: 'prevEmployIndustry'}, value)
                            }
                        />
                    </Grid>
                </>
            )}
            {parameters.currentlyEmployed.value === 'Unemployed' && (
                <>
                    <Grid item xs={4}>
                        <TextBox
                            id={'reasonUnemployed'}
                            label={required('Reason')}
                            value={parameters.reasonUnemployed?.value}
                            onChange={(value) =>
                                valueChanged({...parameters.reasonUnemployed, name: 'reasonUnemployed'}, value)
                            }
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextBox
                            id={'previousJobInfo'}
                            label={required('Previous Employer Name, Time Employed, Occupation')}
                            value={parameters.previousJobInfo?.value}
                            onChange={(value) =>
                                valueChanged({...parameters.previousJobInfo, name: 'previousJobInfo'}, value)
                            }
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextBox
                            id={'previousIncome'}
                            label={required('Previous Income')}
                            value={parameters.previousIncome?.value}
                            onChange={(value) =>
                                valueChanged({...parameters.previousIncome, name: 'previousIncome'}, value)
                            }
                            InputProps={{
                                inputComponent: NumberFormatCustom,
                            }}
                        />
                    </Grid>
                </>
            )}
            {parameters.currentlyEmployed.value === 'Retired' && (
                <>
                    <Grid item xs={4}>
                        <TextBox
                            id={'retiredInfo'}
                            label={required('Age At Retirement & Reason')}
                            value={parameters.retiredInfo?.value}
                            onChange={(value) => valueChanged({...parameters.retiredInfo, name: 'retiredInfo'}, value)}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextBox
                            id={'retiredIncome'}
                            label={required('Provide Details To Income')}
                            value={parameters.retiredIncome?.value}
                            onChange={(value) =>
                                valueChanged({...parameters.retiredIncome, name: 'retiredIncome'}, value)
                            }
                        />
                    </Grid>
                </>
            )}
            {parameters.currentlyEmployed.value === 'Student' && (
                <Grid item xs={4}>
                    <TextBox
                        id={'schoolInfo'}
                        label={required("School name and child's grade")}
                        value={parameters.schoolInfo?.value}
                        onChange={(value) => valueChanged({...parameters.schoolInfo, name: 'schoolInfo'}, value)}
                    />
                </Grid>
            )}
            {parameters.currentlyEmployed.value === 'Disability' && (
                <Grid item xs={4}>
                    <TextBox
                        id={'disabilityInfo'}
                        label={required('Reason for SSDI, how long they have been receiving SSDI')}
                        value={parameters.disabilityInfo?.value}
                        onChange={(value) =>
                            valueChanged({...parameters.disabilityInfo, name: 'disabilityInfo'}, value)
                        }
                    />
                </Grid>
            )}
            {parameters.currentlyEmployed.value === 'Other' && (
                <Grid item xs={4}>
                    <TextBox
                        id={'otherInfo'}
                        label={required('Reason for selecting other')}
                        value={parameters.otherInfo?.value}
                        onChange={(value) => valueChanged({...parameters.otherInfo, name: 'otherInfo'}, value)}
                    />
                </Grid>
            )}
        </>
    );
});

const EmploymentQuestions = observer(({parameters, valueChanged, preapproval, prequal}: EmploymentProps) => {
    return (
        <>
            <Grid item xs={4}>
                <SelectInputElement
                    param={{
                        label: 'Employment Status',
                        options: fieldOptions.currentlyEmployed.options,
                        ...parameters.currentlyEmployed,
                        name: 'currentlyEmployed',
                    }}
                    required={preapproval ? required('') : ''}
                    valueChanged={valueChanged}
                    newLabelType={prequal}
                />
            </Grid>
            {preapproval && <EmploymentReflexives parameters={parameters} valueChanged={valueChanged} />}
        </>
    );
});

export default EmploymentQuestions;
